import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { CaretDown, Check } from '@phosphor-icons/react';

/**
 * Searchable single-select combobox.
 *
 * Props:
 *  - value:   currently selected value
 *  - onChange(value): called with the selected value (or empty string if cleared)
 *  - options: [{ value, label, hint? }]
 *  - placeholder: trigger placeholder when nothing picked
 *  - searchPlaceholder: input placeholder
 *  - emptyText: shown when search has no match
 *  - triggerClassName / disabled / testId
 *  - allowClear: show a "Clear" option at the top
 */
export default function SearchableSelect({
  value = '',
  onChange,
  options = [],
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyText = 'No results',
  triggerClassName = '',
  disabled = false,
  allowClear = false,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          className={`flex items-center justify-between w-full h-9 px-3 text-sm bg-white border border-[#E5E5E5] hover:border-[#0A0A0A] disabled:opacity-50 ${triggerClassName}`}
        >
          <span className={`truncate text-left ${selected ? 'text-[#0A0A0A]' : 'text-[#999]'}`}>
            {selected ? selected.label : placeholder}
          </span>
          <CaretDown size={14} className="text-[#666] ml-2 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} data-testid={testId ? `${testId}-search` : undefined} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {allowClear && value && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => { onChange(''); setOpen(false); }}
                  className="text-[#666] italic"
                >
                  Clear selection
                </CommandItem>
              )}
              {options.map(opt => (
                <CommandItem
                  key={opt.value}
                  value={`${opt.label} ${opt.value} ${opt.hint || ''}`}
                  onSelect={() => { onChange(opt.value); setOpen(false); }}
                  className="flex items-center justify-between"
                  data-testid={testId ? `${testId}-option-${opt.value}` : undefined}
                >
                  <div className="flex flex-col min-w-0">
                    <span className="truncate">{opt.label}</span>
                    {opt.hint && <span className="text-[10px] text-[#666] truncate">{opt.hint}</span>}
                  </div>
                  {opt.value === value && <Check size={14} className="text-[#002FA7] shrink-0" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
